import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PropertyTaxRecord {
  id: string;
  fixed_asset_id: string;
  annual_amount: number;
  due_month: number;
  due_day: number;
  start_year: number;
  notes: string | null;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface PropertyTaxPayment {
  id: string;
  property_tax_id: string;
  user_id: string;
  year_paid: number;
  amount: number;
  payment_date: string;
  receipt_number: string | null;
  notes: string | null;
  transaction_id: string | null;
  created_at: string;
}

export function usePropertyTax(fixedAssetId: string | undefined) {
  return useQuery({
    queryKey: ['property-tax', fixedAssetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('property_tax_records')
        .select('*')
        .eq('fixed_asset_id', fixedAssetId!)
        .maybeSingle();
      if (error) throw error;
      return data as PropertyTaxRecord | null;
    },
    enabled: !!fixedAssetId,
  });
}

export function usePropertyTaxPayments(propertyTaxId: string | undefined) {
  return useQuery({
    queryKey: ['property-tax-payments', propertyTaxId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('property_tax_payments')
        .select('*')
        .eq('property_tax_id', propertyTaxId!)
        .order('year_paid', { ascending: false });
      if (error) throw error;
      return data as PropertyTaxPayment[];
    },
    enabled: !!propertyTaxId,
  });
}

export function useCreatePropertyTax() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (record: Omit<PropertyTaxRecord, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('property_tax_records')
        .insert(record)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['property-tax', variables.fixed_asset_id] });
    },
  });
}

export function useUpdatePropertyTax() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PropertyTaxRecord> & { id: string }) => {
      const { data, error } = await supabase
        .from('property_tax_records')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['property-tax', data.fixed_asset_id] });
    },
  });
}

export function useRecordPropertyTaxPayment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (payment: Omit<PropertyTaxPayment, 'id' | 'created_at' | 'user_id'>) => {
      const { data, error } = await supabase
        .from('property_tax_payments')
        .insert({ ...payment, user_id: user!.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['property-tax-payments', variables.property_tax_id] });
    },
  });
}

export function useDeletePropertyTaxPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentId }: { paymentId: string }) => {
      const { error } = await supabase
        .from('property_tax_payments')
        .delete()
        .eq('id', paymentId);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      // Invalidate everything property tax related to be safe
      qc.invalidateQueries({ queryKey: ['property-tax-payments'] });
    },
  });
}
